import { useState } from 'react';
import { Lock, FileText, Check, AlertCircle, Loader2, Shield, Clock, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { generatePartialPreviewHtml, formatDateFr } from '../utils/previewUtils';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI28q4NOcW936FcvOgjC04";

export default function AttestationHebergementPage() {
  const [formData, setFormData] = useState({
    hebergeur_nom: '',
    hebergeur_prenom: '',
    hebergeur_adresse: '',
    heberge_nom: '',
    heberge_prenom: '',
    lien_heberge: '',
    lieu: '',
    date_document: new Date().toISOString().split('T')[0]
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    return formData.hebergeur_nom && formData.hebergeur_prenom && formData.hebergeur_adresse && 
           formData.heberge_nom && formData.heberge_prenom && formData.lien_heberge &&
           formData.lieu && formData.date_document;
  };
  
  const generatePreviewHtml = () => {
    const { hebergeur_nom, hebergeur_prenom, hebergeur_adresse, heberge_nom, heberge_prenom, lien_heberge, lieu, date_document } = formData;
    
    const introText = `Je soussigné(e) ${hebergeur_prenom} ${hebergeur_nom.toUpperCase()}, demeurant à ${hebergeur_adresse.replace(/\n/g, ', ')}, certifie sur l'honneur héberger à mon domicile :`;
    const content = `${heberge_prenom} ${heberge_nom.toUpperCase()} (${lien_heberge}), à titre gratuit et depuis plus de trois mois à l'adresse mentionnée ci-dessus.`;
    
    return generatePartialPreviewHtml({
      nom: hebergeur_nom,
      prenom: hebergeur_prenom,
      adresse: hebergeur_adresse,
      dateStr: formatDateFr(date_document),
      lieu,
      title: "ATTESTATION D'HÉBERGEMENT",
      content,
      introText
    });
  };
  
  const handleGeneratePreview = () => {
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setPreviewLoading(true);
    setError(null);
    
    setTimeout(() => {
      const html = generatePreviewHtml();
      setPreviewHtml(html);
      setCurrentStep(2);
      setPreviewLoading(false);
    }, 500);
  };
  
  const handlePayment = () => {
    setPaymentLoading(true);
    
    const paymentData = {
      documentId: 'attestation-hebergement',
      documentName: "Attestation d'hébergement",
      formData: formData,
      previewHtml: previewHtml,
      timestamp: Date.now()
    };
    localStorage.setItem('pdf_facile_payment', JSON.stringify(paymentData));
    
    window.location.href = STRIPE_PAYMENT_LINK;
  };
  
  return (
    <>
      <Helmet>
        <title>Attestation d'hébergement PDF – Téléchargement rapide | PDF Facile</title>
        <meta name="description" content="Créez votre attestation d'hébergement en ligne. Document conforme aux usages administratifs français pour certifier qu'une personne est hébergée à votre domicile. Aperçu gratuit, PDF à 4,99€." />
        <link rel="canonical" href="/attestation-hebergement-pdf" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        
        <main className="flex-1">
          <section className="bg-slate-50 py-8 md:py-12">
            <div className="container-app max-w-3xl">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4" data-testid="hebergement-h1">
                Attestation d'hébergement à télécharger en PDF
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Générez une attestation d'hébergement <strong>conforme aux usages administratifs français</strong>. 
                Ce document certifie qu'une personne est hébergée à votre domicile à titre gratuit.
                Prêt en quelques minutes, sans inscription.
              </p>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Document conforme</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Prêt en 2 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Sans inscription</span>
                </div>
              </div>
            </div>
          </section>
          
          <section className="py-4 border-b border-slate-200">
            <div className="container-app max-w-3xl">
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'inactive'}`}>
                    {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Formulaire</span>
                </div>
                <div className={`step-line flex-1 max-w-[60px] ${currentStep > 1 ? 'completed' : ''}`} />
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 2 ? 'active' : 'inactive'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Aperçu & Téléchargement</span>
                </div>
              </div>
            </div>
          </section>
          
          <section className="section-spacing">
            <div className="container-app max-w-2xl">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {currentStep === 1 && (
                <div data-testid="hebergement-form-step">
                  <h2 className="text-lg font-semibold mb-6">Informations de l'hébergeur</h2>
                  
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="hebergeur_nom" className="label-field">
                          Nom de l'hébergeur <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="hebergeur_nom"
                          type="text"
                          placeholder="Dupont"
                          value={formData.hebergeur_nom}
                          onChange={(e) => handleInputChange('hebergeur_nom', e.target.value)}
                          className="input-field"
                          data-testid="field-hebergeur-nom"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="hebergeur_prenom" className="label-field">
                          Prénom de l'hébergeur <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="hebergeur_prenom"
                          type="text"
                          placeholder="Jean"
                          value={formData.hebergeur_prenom}
                          onChange={(e) => handleInputChange('hebergeur_prenom', e.target.value)}
                          className="input-field"
                          data-testid="field-hebergeur-prenom"
                        />
                      </div>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="hebergeur_adresse" className="label-field">
                        Adresse de l'hébergeur <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="hebergeur_adresse"
                        placeholder="123 rue de la Paix, 75001 Paris"
                        value={formData.hebergeur_adresse}
                        onChange={(e) => handleInputChange('hebergeur_adresse', e.target.value)}
                        className="input-field min-h-[80px] py-3"
                        data-testid="field-hebergeur-adresse"
                      />
                    </div>
                    
                    <h2 className="text-lg font-semibold mt-8 mb-4">Informations de la personne hébergée</h2>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="heberge_nom" className="label-field">
                          Nom de l'hébergé <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="heberge_nom"
                          type="text"
                          placeholder="Martin"
                          value={formData.heberge_nom}
                          onChange={(e) => handleInputChange('heberge_nom', e.target.value)}
                          className="input-field"
                          data-testid="field-heberge-nom"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="heberge_prenom" className="label-field">
                          Prénom de l'hébergé <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="heberge_prenom"
                          type="text"
                          placeholder="Marie"
                          value={formData.heberge_prenom}
                          onChange={(e) => handleInputChange('heberge_prenom', e.target.value)}
                          className="input-field"
                          data-testid="field-heberge-prenom"
                        />
                      </div>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="lien_heberge" className="label-field">
                        Lien avec l'hébergé <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="lien_heberge"
                        type="text"
                        placeholder="Fils, fille, ami(e), cousin(e)..."
                        value={formData.lien_heberge}
                        onChange={(e) => handleInputChange('lien_heberge', e.target.value)}
                        className="input-field"
                        data-testid="field-lien-heberge"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                      <div className="form-field">
                        <Label htmlFor="lieu" className="label-field">
                          Lieu <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="lieu"
                          type="text"
                          placeholder="Paris"
                          value={formData.lieu}
                          onChange={(e) => handleInputChange('lieu', e.target.value)}
                          className="input-field"
                          data-testid="field-lieu"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="date_document" className="label-field">
                          Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="date_document"
                          type="date"
                          value={formData.date_document}
                          onChange={(e) => handleInputChange('date_document', e.target.value)}
                          className="input-field"
                          data-testid="field-date"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Button
                      onClick={handleGeneratePreview}
                      disabled={!validateForm() || previewLoading}
                      className="btn-primary w-full"
                      data-testid="generate-preview-btn"
                    >
                      {previewLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5 mr-2" />
                          Générer l'aperçu gratuitement
                        </>
                      )}
                    </Button>
                    
                    {/* Réassurance sous le bouton */}
                    <div className="mt-6 text-xs text-slate-500 space-y-1">
                      <p>• Documents conformes aux usages administratifs français</p>
                      <p>• Aperçu partiel gratuit avant paiement</p>
                      <p>• Téléchargement immédiat après paiement</p>
                      <p>• Aucune inscription requise</p>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep === 2 && previewHtml && (
                <div data-testid="hebergement-preview-step">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Aperçu de votre attestation</h2>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-accent hover:underline"
                    >
                      Modifier
                    </button>
                  </div>
                  
                  <div 
                    className="preview-document mb-6"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                  
                  <div className="bg-slate-50 rounded-lg p-6 text-center">
                    <div className="payment-badge inline-flex mb-4">
                      <Lock className="w-4 h-4" />
                      <span>Paiement sécurisé</span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">
                      Votre attestation est prête
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      Le PDF sera identique à l'aperçu ci-dessus, au format A4.
                    </p>
                    
                    <Button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className="btn-primary w-full"
                      data-testid="pay-button"
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Télécharger le PDF – 4,99 €
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Paiement unique • Sans abonnement • Téléchargement immédiat
                    </p>
                    
                    <p className="text-xs text-slate-400 mt-3">
                      Paiement sécurisé via Stripe – transactions protégées par chiffrement SSL
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
          
          <section className="bg-slate-50 section-spacing">
            <div className="container-app max-w-3xl">
              <h2 className="text-xl font-semibold mb-4">
                Qu'est-ce qu'une attestation d'hébergement ?
              </h2>
              <div className="prose prose-slate max-w-none text-muted-foreground">
                <p>
                  L'attestation d'hébergement est un document officiel par lequel une personne (l'hébergeur) 
                  certifie qu'elle accueille à son domicile une autre personne (l'hébergé) à titre gratuit. 
                  Ce document est couramment demandé lors de démarches administratives : ouverture de compte bancaire, 
                  inscription à Pôle emploi, demande de carte grise ou de pièce d'identité.
                </p>
                <p>
                  Pour être valable, l'attestation d'hébergement doit généralement être accompagnée de pièces 
                  justificatives : une copie de la pièce d'identité de l'hébergeur et un justificatif de domicile 
                  récent (facture d'électricité, quittance de loyer, avis d'imposition).
                </p>
                <p>
                  <strong>Important :</strong> L'hébergeur engage sa responsabilité sur l'exactitude des informations 
                  déclarées. Une fausse attestation d'hébergement est passible de sanctions pénales 
                  (article 441-7 du Code pénal).
                </p>
                <p>
                  Notre modèle respecte les usages administratifs français et inclut tous les éléments requis : 
                  identité de l'hébergeur et de l'hébergé, adresse du logement, date et signature.
                </p>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
